import {
  ActionPlanIntervention,
  DtoEnvelopeActionPlanIntervention,
  InterventionStatus,
} from '@/api/generated-catch-backend/model';
import ModalInterventionDetails from '@/app/components/action-plan/components/modals/details/modal-intervention-details';
import { Intervention } from '@/app/components/action-plan/types';
import icons from '@/assets/icons';
import { ROUTE_ACTION_PLAN, ROUTE_BUILD_ACTION_PLAN } from '@/constants/routes';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecommendedActionCard } from './components/recommended-action-card';
import { convertToLocalIntervention } from './utils/interventionMapper';

interface RecommendedActionsProps {
  recommendedActions?: DtoEnvelopeActionPlanIntervention;
}

// Helper function to validate if an intervention has essential data for display
export const interventionHasEssentialData = (intervention: ActionPlanIntervention): boolean => {
  return !!(intervention.title && intervention.title.trim() !== '' && intervention.id && intervention.category);
};

export default function RecommendedActions({ recommendedActions }: RecommendedActionsProps) {
  const navigate = useNavigate();
  const { setNavigationSource } = useAnalyticsCalls();
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Show skeleton loader while data is loading
  if (!recommendedActions) {
    return (
      <div className="risk-cards-container">
        <div className="w-full max-w-[650px]">
          <div className="h-7 w-48 animate-pulse rounded-full bg-gray-300" />
          <div className="mt-2 h-5 w-3/4 animate-pulse rounded-full bg-gray-200" />
        </div>

        <div className="flex w-full max-w-[650px] flex-col gap-3">
          {[1, 2, 3].map((index) => (
            <div
              key={`skeleton-${index}`}
              className="md:w-[650px] rounded-[10px] border border-[#ededed] bg-white"
            >
              <div className="flex items-center gap-3 pl-2 pr-4 py-3">
                {/* Skeleton icon on the left */}
                <div className="h-[36px] w-[36px] flex-shrink-0 animate-pulse rounded-lg bg-gray-200" />

                {/* Skeleton content in the middle */}
                <div className="flex-1 items-start text-left">
                  <div className="h-[18px] w-3/4 animate-pulse rounded bg-gray-300" />
                  <div className="mt-[2px] h-[16px] w-1/2 animate-pulse rounded bg-gray-200" />
                </div>

                {/* Skeleton arrow on the right */}
                <div className="h-[39px] w-[39px] flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
              </div>
            </div>
          ))}
        </div>

        <div className="cta-button-container">
          <div className="h-10 w-[293px] animate-pulse rounded-full bg-gray-300" />
        </div>
      </div>
    );
  }

  // Partitioning data into multiple groups (IN_PROGRESS and AVAILABLE) in one pass
  const { inProgressActions, availableActions } = recommendedActions.data.reduce(
    (acc, intervention) => {
      // Only include interventions that have essential data
      if (!interventionHasEssentialData(intervention)) {
        return acc;
      }

      if (intervention.status === InterventionStatus.IN_PROGRESS) {
        acc.inProgressActions.push(intervention);
      } else if (intervention.status === InterventionStatus.AVAILABLE) {
        acc.availableActions.push(intervention);
      }
      return acc;
    },
    { inProgressActions: [] as ActionPlanIntervention[], availableActions: [] as ActionPlanIntervention[] },
  );
  const hasActiveActionPlan = inProgressActions.length > 0;
  const hasActions = inProgressActions.length > 0 || availableActions.length > 0;
  const topActions = hasActiveActionPlan
    ? inProgressActions.slice(0, 3)
    : hasActions
      ? availableActions.slice(0, 3)
      : ([] as ActionPlanIntervention[]);

  // Helper function to render action icon
  const renderActionIcon = (action: ActionPlanIntervention) => {
    if (action.iconAsset?.url) {
      return <img src={action.iconAsset.url} alt={action.iconAsset.alt || action.title} className="h-auto w-[36px]" />;
    }

    // Default to biomarkers-alt-1 icon when no specific icon is available
    const iconSrc = icons['biomarkers-alt-1'];
    return <img src={iconSrc} alt={action.title} className="h-auto w-[36px]" />;
  };

  return (
    <div className="risk-cards-container">
      <div className="w-full max-w-[650px]">
        <h2 className="mobile-h3-primary text-primary text-left font-bold">
          {hasActiveActionPlan ? 'Your Action Plan' : 'Your Recommended Actions'}
        </h2>
        <AnimatePresence mode="wait">
          <motion.p
            className="body-regular text-primary text-left pt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {hasActiveActionPlan
              ? 'Complete these actions to reduce your risk.'
              : hasActions
                ? 'These are the top changes we recommend to reduce your risk.'
                : "You've completed all of your actions."}
          </motion.p>
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          className="flex w-full max-w-[650px] flex-col gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {topActions.map((action) => (
            <RecommendedActionCard
              key={action.id}
              onClick={() => {
                setNavigationSource(DashboardSource.DASHBOARD_SPECIFIC_ACTION);
                setSelectedIntervention(convertToLocalIntervention(action));
                setIsModalOpen(true);
              }}
              action={action}
            />
          ))}
        </motion.div>
      </AnimatePresence>

      {hasActiveActionPlan ? (
        <div className="cta-button-container">
          <button
            onClick={() => {
              setNavigationSource(DashboardSource.DASHBOARD_ACTION_CTA);
              navigate(ROUTE_ACTION_PLAN);
            }}
            className="cta-button body-meta"
          >
            See Your Action Plan
          </button>
        </div>
      ) : (
        <div className="cta-button-container">
          <button
            onClick={() => {
              setNavigationSource(DashboardSource.DASHBOARD_ACTION_CTA);
              navigate(ROUTE_BUILD_ACTION_PLAN);
            }}
            className="cta-button body-meta"
          >
            See Your Recommended Actions
          </button>
        </div>
      )}

      {selectedIntervention && (
        <ModalInterventionDetails
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedIntervention(null);
          }}
          intervention={selectedIntervention}
          navToActionPlanHome={hasActiveActionPlan}
        />
      )}
    </div>
  );
}
